import React from 'react';
import { UNSAFE_PortalProvider as AriaPortalProvider } from 'react-aria';
import { useIsHidden } from 'react-aria/private/collections/Hidden';
import { Popover as AriaPopover } from 'react-aria-components';

import { tv } from '../../tv';
import { portalStyles, surfacedOverlay, useOverlayPresence } from './overlay-portal';
import { OVERLAY_OFFSET, RAC_PLACEMENT } from './placement';

/**
 * A picker's popup (Select, Combobox) on the mount-on-open portal. Internal
 * to ComplyKit, imported directly. Two things Menu's and Popover's popups do
 * not have to get right (picker.md):
 *
 * 1. React Aria's Select and ComboBox build their option collection in a
 *    hidden render pass while the picker is closed. React Aria's own Popover
 *    renders its children bare in that pass; a popup that returned null before
 *    reaching it would leave the collection empty: no value while closed, no
 *    typeahead, no options in the hidden form select. So: hidden pass means
 *    children bare. The hook comes from the private path React Aria's own
 *    Popover imports it from; a move fails loudly at import time.
 *
 * 2. A ComboBox hides everything outside its input and listbox from assistive
 *    technology the moment it opens, and keeps hiding whatever is added to the
 *    body afterwards. A container that appears one render after the popup
 *    opened is hidden with the rest. So the container element exists before
 *    the popup renders into it and is attached to the body in an insertion
 *    effect, before the popup measures itself and before the ComboBox walks
 *    the document. It is detached again once the popup is gone, so a closed
 *    picker leaves nothing in the body (overlays.md, the portal convention).
 */

/* The surfaced-overlay recipe with the picker's width rule: the trigger's
 * width (React Aria's --trigger-width, from the popup's trigger ref) above
 * Menu's 180px floor, no cap. A column, not a scroller: the list inside is
 * the scroll container, so a filter head stays put while the options scroll
 * and the keyboard delegate pages a list it can measure. */
const popupStyles = tv({
  base: [...surfacedOverlay, 'ck:min-w-45 ck:flex ck:flex-col'],
});

function PickerPortal({
  name,
  isPresent,
  children,
}: {
  name: string;
  isPresent: boolean;
  children: React.ReactNode;
}) {
  const [container] = React.useState(() => {
    if (typeof document === 'undefined') return null;
    const el = document.createElement('div');
    el.className = portalStyles();
    el.setAttribute('data-component', `${name}-portal`);
    // A Chakra dialog's focus lock pulls focus out of any portal outside it, closing the picker.
    el.setAttribute('data-no-focus-lock', 'true');
    return el;
  });
  React.useInsertionEffect(() => {
    if (!container || !isPresent) return;
    document.body.appendChild(container);
    return () => container.remove();
  }, [container, isPresent]);
  if (!container || !isPresent) return null;
  return <AriaPortalProvider getContainer={() => container}>{children}</AriaPortalProvider>;
}

export interface PickerPopupProps {
  /** The picker's open state, read from its React Aria state by the caller. */
  isOpen: boolean;
  /** The element the popup anchors and sizes against, when it is not the opening button. */
  triggerRef?: React.RefObject<HTMLElement | null>;
  /** The portal container's name: `${name}-portal`. */
  name: string;
  /** Called as the popup mounts and once it has fully left, its exit included. */
  onPresenceChange?: (isPresent: boolean) => void;
  children: React.ReactNode;
}

export const PickerPopup = React.forwardRef<HTMLElement, PickerPopupProps>(function PickerPopup(
  { isOpen, triggerRef, name, onPresenceChange, children },
  forwardedRef,
) {
  const isHidden = useIsHidden();
  const { popupRef, isPresent } = useOverlayPresence<HTMLElement>(isOpen, forwardedRef);
  React.useEffect(() => {
    if (!isHidden) onPresenceChange?.(isPresent);
  }, [isHidden, isPresent, onPresenceChange]);
  if (isHidden) return children;
  return (
    <PickerPortal name={name} isPresent={isPresent}>
      <AriaPopover
        ref={popupRef}
        triggerRef={triggerRef}
        placement={RAC_PLACEMENT['bottom-start']}
        offset={OVERLAY_OFFSET}
        className={popupStyles()}
        style={
          {
            zIndex: 'var(--ck-z-overlay)',
            width: 'var(--trigger-width)',
          } as React.CSSProperties
        }
        data-component={`${name}-popup`}
      >
        {children}
      </AriaPopover>
    </PickerPortal>
  );
});
